import React, { useEffect, useRef, useContext } from 'react';
import { AlloyTypes } from './AlloyRepairRequestTypes';
import Images from '../../../images/AlloyMainDetailsImages';
import TextInput from '../../common/textInput/TextInput';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import FormInput from '../../common/FormInput';
import AddressManager from '../../common/address/AddressManager';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import { AlloyRepairRequestContext } from '../../../context/AlloyRepairRequestContext';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { Locations, PageNames } from '../../../Constants/Common';
import { AddressType, ImageOption } from '../../common/CommonTypes';
import SelectableImageMultiple from '../../common/SelectableImageMultiple';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useLocation } from 'react-router-dom';

export type AlloyMainDetailsProps = {
    cancelConfirmText: string;
};

const AlloyMainDetails = ({
    cancelConfirmText
}: AlloyMainDetailsProps): JSX.Element => {
    const modalAddress = useRef(null);

    const { t, i18n } = useTranslation();

    const location = useLocation();
    const { selectedDealerAddress } = location.state;

    const isSiteLocationUs = i18n.language?.toLowerCase() === Locations.Us;
    const {
        alloyRepairRequest,
        onSelectAlloyType,
        onChangeMileage,
        onRepairAddressSelectionChanged,
        onContinueRepairRequest,
        onCancelClick,
        onCancelRepairRequest,
        onCancelUndo,
        onRepairAddressUpdate
    } = useContext(AlloyRepairRequestContext);

    const {
        alloyType,
        mileage,
        selectedWheels,
        scrollToAddress,
        repairAddresses,
        repairAddressSelectedIndex,
        alloyFormErrors,
        showCancelAlert
    } = alloyRepairRequest;

    const alloyTypeOptions: ImageOption[] = [
        {
            name: AlloyTypes.StandardAlloy,
            image: Images.AlloyWheelStandard,
            altText: AlloyTypes.StandardAlloy,
            label: t('alloy_repair_request.alloy_types.standard_alloy')
        },
        {
            name: AlloyTypes.DiamondCut,
            image: Images.AlloyWheelDiamond,
            altText: AlloyTypes.DiamondCut,
            label: t('alloy_repair_request.alloy_types.diamond_cut')
        },
        {
            name: AlloyTypes.TwoTone,
            image: Images.AlloyWheelTwoTone,
            altText: AlloyTypes.TwoTone,
            label: t('alloy_repair_request.alloy_types.two_tone')
        }
    ];

    useEffect(() => {
        if (isSiteLocationUs && selectedDealerAddress && !scrollToAddress) {
            let repairAddress: AddressType = undefined;

            if (!selectedDealerAddress.dealerStreetAddress) {
                repairAddress = { ...selectedDealerAddress };
            } else {
                repairAddress = {
                    addressLine1:
                        selectedDealerAddress.dealerStreetAddress.street || '',
                    townOrCity:
                        selectedDealerAddress.dealerStreetAddress.city || '',
                    postcode:
                        selectedDealerAddress.dealerStreetAddress
                            .zipOrPostalCode || '',
                    country: selectedDealerAddress.locationCountry || ''
                };
            }

            if (
                !repairAddresses.find(
                    (address) =>
                        JSON.stringify(address) ===
                        JSON.stringify(repairAddress)
                )
            ) {
                onRepairAddressUpdate(repairAddress);
            }
        }
        if (scrollToAddress && modalAddress.current) {
            modalAddress.current.focus();
        }
        GoogleAnalytics.setPageView(PageNames.AlloyGeneral);
    }, []);

    const errorlist = (): Array<string | undefined> => {
        const values = [
            alloyFormErrors?.alloyType,
            alloyFormErrors?.mileage,
            alloyFormErrors?.noRepairAddressSelected
        ];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };
    const forCancelButton = () => {
        const buttonText =
            selectedWheels.length > 1
                ? t('cancel_repair_requests')
                : t('cancel_repair_request');
        return (
            <button
                data-testid='danger-button'
                className='btn btn-danger progress-button'
                onClick={onCancelClick}>
                {buttonText}
            </button>
        );
    };
    const cancelButton = forCancelButton();

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='alloy-type-forminput'
                        label={t('alloy_repair_request.section_1_title')}
                        helpText={t('help_text.alloy_type')}
                        errorText={alloyFormErrors?.alloyType}>
                        <SelectableImageMultiple
                            imageOptions={alloyTypeOptions}
                            selectedItem={alloyType}
                            onChange={onSelectAlloyType}
                        />
                    </FormInput>
                    <FormInput
                        id='mileage-forminput'
                        label={t('alloy_repair_request.section_2_title')}
                        errorText={alloyFormErrors?.mileage}>
                        <div className='row repair-request-field-container'>
                            <div className='col-xs-12 col-lg-6'>
                                <TextInput
                                    name='approx-mileage'
                                    value={mileage?.toString() || ''}
                                    onChange={onChangeMileage}
                                />
                            </div>
                        </div>
                    </FormInput>
                    <FormInput
                        id='repair-address-forminput'
                        label={t('alloy_repair_request.section_3_title')}
                        errorText={alloyFormErrors?.noRepairAddressSelected}>
                        <div className='ml-3'>
                            <p>{t('alloy_repair_request.section_3_para_1')}</p>
                            {i18next.language.toLocaleLowerCase() !== 'us' && (
                                <p>
                                    {t('alloy_repair_request.section_3_para_2')}
                                </p>
                            )}
                        </div>
                        {isSiteLocationUs && selectedDealerAddress && (
                            <AddressManager
                                addressSelectedIndex={
                                    repairAddressSelectedIndex
                                }
                                addresses={repairAddresses}
                                onAddressSelectionChanged={
                                    onRepairAddressSelectionChanged
                                }
                            />
                        )}
                        {!isSiteLocationUs && (
                            <AddressManager
                                addressSelectedIndex={
                                    repairAddressSelectedIndex
                                }
                                addresses={repairAddresses}
                                onAddressSelectionChanged={
                                    onRepairAddressSelectionChanged
                                }
                            />
                        )}
                        <div tabIndex={0} ref={modalAddress}></div>
                    </FormInput>
                </div>
                <div className='card-footer page-content-footer'>
                    {(alloyFormErrors?.alloyType ||
                        alloyFormErrors?.mileage ||
                        alloyFormErrors?.noRepairAddressSelected) && (
                        <ErrorSummary
                            title={t('general_errors')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onContinueRepairRequest}>
                    {t('continue')}
                </button>
                {cancelButton}
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
        </>
    );
};

export default AlloyMainDetails;
